import type {
  FormManagementExportDetailColumn,
  FormManagementExportDetailRow,
  FormManagementExportFormatId,
} from '@/contracts/form-management'

export type FormManagementExportFile = {
  format: FormManagementExportFormatId
  fileName: string
  mimeType: string
  content: string
  rowCount: number
  source: 'synthetic_mock' | 'd1_response_rows' | 'd1_worker_stream' | 'd1_r2_export'
  mockSafe: boolean
}

type BuildFormManagementExportFileInput = {
  format: FormManagementExportFormatId
  formTitle: string
  fileNameScope?: string
  columns: FormManagementExportDetailColumn[]
  rows: FormManagementExportDetailRow[]
  source?: FormManagementExportFile['source']
  generatedAt?: Date
}

export function buildFormManagementExportFile({
  format,
  formTitle,
  fileNameScope,
  columns,
  rows,
  source = 'synthetic_mock',
  generatedAt = new Date(),
}: BuildFormManagementExportFileInput): FormManagementExportFile {
  const scopeSegment = fileNameScope ? `-${sanitizeFileName(fileNameScope)}` : ''
  const fileBase = `${sanitizeFileName(formTitle)}-responses${scopeSegment}-${formatTimestamp(generatedAt)}`
  const rowValues = rows.map((row) => columns.map((column) => row.cells[column.id] ?? '-'))
  const mockSafe = source === 'synthetic_mock'

  if (format === 'csv') {
    return {
      format,
      fileName: `${fileBase}.csv`,
      mimeType: 'text/csv;charset=utf-8',
      content: withUtf8Bom(buildCsvContent(columns, rowValues)),
      rowCount: rows.length,
      source,
      mockSafe,
    }
  }

  if (format === 'excel') {
    return {
      format,
      fileName: `${fileBase}.xls`,
      mimeType: 'application/vnd.ms-excel;charset=utf-8',
      content: buildTableHtml({
        title: formTitle,
        columns,
        rowValues,
        generatedAt,
        source,
        printOptimized: false,
      }),
      rowCount: rows.length,
      source,
      mockSafe,
    }
  }

  if (format === 'pdf') {
    return {
      format,
      fileName: `${fileBase}.pdf.html`,
      mimeType: 'text/html;charset=utf-8',
      content: buildTableHtml({
        title: `${formTitle} PDF 列印版`,
        columns,
        rowValues,
        generatedAt,
        source,
        printOptimized: true,
      }),
      rowCount: rows.length,
      source,
      mockSafe,
    }
  }

  return {
    format,
    fileName: `${fileBase}.json`,
    mimeType: 'application/json;charset=utf-8',
    content: JSON.stringify(
      {
        source,
        format: 'json',
        generatedAt: generatedAt.toISOString(),
        rowCount: rows.length,
        columns,
        rows: rows.map((row) => ({
          id: row.id,
          recordNo: row.recordNo,
          responseId: row.responseId,
          submittedAt: row.submittedAt,
          statusLabel: row.statusLabel,
          respondentLabel: row.respondentLabel,
          cells: row.cells,
        })),
      },
      null,
      2,
    ),
    rowCount: rows.length,
    source,
    mockSafe,
  }
}

export function downloadFormManagementExportFile(file: FormManagementExportFile) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return false

  const blob = new Blob([file.content], { type: file.mimeType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = file.fileName
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
  return true
}

function buildCsvContent(columns: FormManagementExportDetailColumn[], rowValues: string[][]) {
  const header = columns.map((column) => escapeCsvValue(column.label)).join(',')
  const rows = rowValues.map((row) =>
    row.map((value, index) => escapeCsvValue(value, columns[index])).join(','),
  )
  return [header, ...rows].join('\r\n')
}

function withUtf8Bom(value: string) {
  return value.startsWith('\uFEFF') ? value : `\uFEFF${value}`
}

function buildTableHtml({
  title,
  columns,
  rowValues,
  generatedAt,
  source,
  printOptimized,
}: {
  title: string
  columns: FormManagementExportDetailColumn[]
  rowValues: string[][]
  generatedAt: Date
  source: FormManagementExportFile['source']
  printOptimized: boolean
}) {
  const headerCells = columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')
  const bodyRows = rowValues
    .map(
      (row) =>
        `<tr>${row.map((value, index) => buildHtmlTableCell(value, columns[index])).join('')}</tr>`,
    )
    .join('')

  return `<!doctype html>
<html lang="zh-Hant">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, "Microsoft JhengHei", sans-serif; color: #0f172a; margin: 24px; }
    h1 { font-size: 20px; margin: 0 0 8px; }
    p { color: #475569; font-size: 12px; margin: 0 0 16px; }
    table { border-collapse: collapse; width: 100%; font-size: 12px; }
    th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f1f5f9; color: #334155; font-weight: 700; }
    td { mso-number-format: "\\@"; }
    ${printOptimized ? '@media print { body { margin: 12mm; } table { page-break-inside: auto; } tr { page-break-inside: avoid; page-break-after: auto; } }' : ''}
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p>${escapeHtml(getExportSourceDescription(source))} Generated at ${escapeHtml(generatedAt.toISOString())}. No backend export job or external storage is used.</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`
}

function getExportSourceDescription(source: FormManagementExportFile['source']) {
  if (source === 'd1_r2_export') {
    return 'D1 response detail rows from an R2-backed export job.'
  }
  if (source === 'd1_worker_stream') {
    return 'D1 response detail rows from a worker-stream export job.'
  }
  if (source === 'd1_response_rows') {
    return 'D1 response detail rows for browser-local export.'
  }
  return 'Mock-safe synthetic export.'
}

function buildHtmlTableCell(value: string, column?: FormManagementExportDetailColumn) {
  const formatAttr = column?.spreadsheetFormat === 'text' ? ' data-format="text"' : ''
  return `<td${formatAttr}>${escapeHtml(protectSpreadsheetFormula(value))}</td>`
}

function escapeCsvValue(value: string, column?: FormManagementExportDetailColumn) {
  const textValue = formatSpreadsheetTextCsvValue(value, column)
  if (textValue) return textValue

  const protectedValue = protectSpreadsheetFormula(value)
  if (!/[",\r\n]/.test(protectedValue)) return protectedValue
  return `"${protectedValue.replaceAll('"', '""')}"`
}

function formatSpreadsheetTextCsvValue(value: string, column?: FormManagementExportDetailColumn) {
  if (column?.spreadsheetFormat !== 'text') return null
  if (value === '-' || value.trim() === '') return null
  if (/^\d+$/.test(value)) return `="${value}"`
  return null
}

function protectSpreadsheetFormula(value: string) {
  if (/^[=+@]/.test(value)) return `'${value}`
  if (
    (value.startsWith('-') && value.length > 1) ||
    value.startsWith('\t') ||
    value.startsWith('\r')
  ) {
    return `'${value}`
  }
  return value
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function sanitizeFileName(value: string) {
  const controlCharacterRange = '\\u0000-\\u001F'
  const invalidFileNamePattern = new RegExp(`[<>:"/\\\\|?*${controlCharacterRange}]+`, 'g')
  const sanitized = value
    .trim()
    .replace(invalidFileNamePattern, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.\s-]+|[.\s-]+$/g, '')
  return sanitized || 'miaoform'
}

function formatTimestamp(value: Date) {
  const parts = [
    value.getUTCFullYear(),
    String(value.getUTCMonth() + 1).padStart(2, '0'),
    String(value.getUTCDate()).padStart(2, '0'),
    '-',
    String(value.getUTCHours()).padStart(2, '0'),
    String(value.getUTCMinutes()).padStart(2, '0'),
    String(value.getUTCSeconds()).padStart(2, '0'),
  ]
  return parts.join('')
}
